import React, { useState } from "react";
import { IconCamera } from "@tabler/icons-react";
import MAvatarEditorDialog from "./MAvatarEditorDialog";
import { mediaService } from "@/services/media.service";
import { membersService } from "@/services/members.service";
import { useAvatarContext } from "@/context/AvatarContext";
import type { AvatarContextType } from "@/types/avatar";
import type { MediaDto } from "@/types/media";

export type MAvatarSize = "xs" | "sm" | "md" | "lg" | "xl" | number;
export type MAvatarType = "dp" | "no-dp";

interface MAvatarProps {
  size?: MAvatarSize;
  px?: number;
  type?: MAvatarType;
  src?: string; // for dp
  text?: string; // for no-dp
  textSize?: number; // font size for initials
  showBadge?: boolean;
  badgeIcon?: React.ReactNode;
  alt?: string;
  className?: string;
  memberId?: string; // Needed for updating member avatar
}

const sizeMap = {
  xs: 20,
  sm: 36,
  md: 48,
  lg: 64,
  xl: 72,
};

const MAvatar: React.FC<MAvatarProps> = ({
  size = "md",
  px: customPx,
  type = "no-dp",
  src,
  text,
  textSize,
  showBadge = false,
  badgeIcon,
  alt = "",
  className = "",
  memberId,
}) => {
  const { _reloadAvatar } = useAvatarContext();
  const px = typeof customPx === "number" ? customPx : sizeMap[size];
  // Only use last name if present for initials/text
  const getDisplayText = () => {
    if (!text) return "";
    const [first, last] = text.split(" ");
    return last
      ? `${first[0]}${last[0]}`.toUpperCase()
      : `${first[0]}`.toUpperCase();
  };

  const [editorOpen, setEditorOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(src);
  React.useEffect(() => {
    setAvatarUrl(src);
  }, [src]);
  const [uploading, setUploading] = useState(false);

  const handleBadgeClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!avatarUrl && memberId && src && src !== "") {
      // src is likely the fileKey (not a signed URL)
      try {
        // Assume mediaService.getSignedUrl returns a signed URL for fileKey
        const signedUrl = await mediaService.getSignedUrl(memberId, src);
        setAvatarUrl(signedUrl);
        setEditorOpen(true);
      } catch {
        setEditorOpen(true); // open anyway, fallback to blank
      }
    } else {
      setEditorOpen(true);
    }
  };

  const handleAvatarSave = async (file: File) => {
    setUploading(true);
    try {
      if (!memberId) {
        throw new Error("Member ID is required for avatar upload");
      }
      // Upload to S3 with memberId
      const media = await mediaService.upload(file, memberId);
      const fileKey = media.fileKey || media.key;
      if (!fileKey) throw new Error("No fileKey returned from media upload");
      // Update member avatar field with fileKey only
      await membersService.updateMember(memberId, { avatar: fileKey } as Record<
        string,
        unknown
      >);
      // Update localStorage: if selectedMember exists and matches, update its avatar; else update member
      const selectedMemberStr = localStorage.getItem("selectedMember");
      if (selectedMemberStr) {
        const selectedMember = JSON.parse(selectedMemberStr);
        if (
          selectedMember &&
          (selectedMember.id === memberId || selectedMember._id === memberId)
        ) {
          selectedMember.avatar = fileKey;
          localStorage.setItem(
            "selectedMember",
            JSON.stringify(selectedMember)
          );
        }
      } else {
        const memberStr = localStorage.getItem("member");
        if (memberStr) {
          const member = JSON.parse(memberStr);
          if (member && (member.id === memberId || member._id === memberId)) {
            member.avatar = fileKey;
            localStorage.setItem("member", JSON.stringify(member));
          }
        }
      }
      setEditorOpen(false);
      window.location.reload();
    } catch (err) {
      // Optionally handle error

      console.error(err);
      alert("Failed to upload avatar. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleAvatarDelete = async () => {
    setUploading(true);
    try {
      if (!memberId) {
        throw new Error("Member ID is required for avatar delete");
      }
      await membersService.updateMember(memberId, { avatar: null } as Record<
        string,
        unknown
      >);

      const selectedMemberStr = localStorage.getItem("selectedMember");
      if (selectedMemberStr) {
        const selectedMember = JSON.parse(selectedMemberStr);
        if (
          selectedMember &&
          (selectedMember.id === memberId || selectedMember._id === memberId)
        ) {
          selectedMember.avatar = null;
          localStorage.setItem(
            "selectedMember",
            JSON.stringify(selectedMember)
          );
        }
      } else {
        const memberStr = localStorage.getItem("member");
        if (memberStr) {
          const member = JSON.parse(memberStr);
          if (member && (member.id === memberId || member._id === memberId)) {
            member.avatar = null;
            localStorage.setItem("member", JSON.stringify(member));
          }
        }
      }

      setAvatarUrl(undefined);
      setEditorOpen(false);
      setTimeout(() => {
        window.location.reload();
      }, 50);
    } catch (err) {
      console.error(err);
      alert("Failed to delete avatar. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <div
        className={`relative flex items-center justify-center flex-shrink-0 ${className}`}
        style={{
          width: size === 24 ? 24 : px,
          height: size === 24 ? 24 : px,
          padding: 0,
          borderRadius: "80px",
          background:
            type === "no-dp"
              ? "var(--colour-background-brand-primary-100, #C8B7E0)"
              : undefined,
          cursor: showBadge ? "pointer" : "default",
        }}
        onClick={showBadge ? () => setEditorOpen(true) : undefined}
      >
        {type === "dp" && avatarUrl ? (
          <img
            src={avatarUrl}
            alt={alt}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "80px",
            }}
          />
        ) : (
          !!text && (
            <span
              style={{
                color: "var(--colour-text-primary)",
                textAlign: "center",
                fontFamily: "var(--typography-font-family-base, Manrope)",
                fontSize: textSize
                  ? `${textSize}px`
                  : size === "xs"
                  ? "11px"
                  : size === "xl"
                  ? "20px"
                  : "16px",
                fontStyle: "normal",
                fontWeight: 600,
                lineHeight: "140%",
                userSelect: "none",
                alignItems: "center",
              }}
            >
              {getDisplayText()}
            </span>
          )
        )}
        {showBadge && (
          <button
            className="profile-avatar-edit-btn"
            aria-label="Edit Avatar"
            type="button"
            style={{
              display: "flex",
              padding: 4,
              justifyContent: "center",
              alignItems: "center",
              gap: 10,
              position: "absolute",
              right: -10,
              bottom: -7,
              borderRadius: 20,
              background: "var(--colour-background-surface-lvl-2, #FFF)",
              boxShadow: "0px 2px 10px 1px rgba(0, 0, 0, 0.12)",
              cursor: uploading ? "progress" : "pointer",
              pointerEvents: uploading ? "none" : "auto",
              border: "none",
              zIndex: 2,
            }}
            onClick={handleBadgeClick}
            disabled={uploading}
          >
            {badgeIcon || <IconCamera size={16} color="#45168C" />}
          </button>
        )}
      </div>
      <MAvatarEditorDialog
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSave={handleAvatarSave}
        initialImage={avatarUrl}
        onDelete={avatarUrl && memberId ? handleAvatarDelete : undefined}
      />
    </>
  );
};

export default MAvatar;
